import { HeapPHIDecorator } from "@app/components/analytics";
import React, { FC, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import {
  GetValidationError,
  SetValidationError,
  ClearValidationError,
} from "@natera/platform/lib/hooks/useError";
import { Textfield } from "@natera/form";
import { FormField, PhoneField } from "@app/components";
import ArrowDownGrayIcon from "@assets/svg/icons/arrowDownGray.svg";
import PatientIcon from "@assets/svg/icons/patientIcon.svg";
import MobilePhoneIcon from "@assets/svg/icons/mobilePhoneIcon.svg";
import DateOfBirthIcon from "@assets/svg/icons/dateOfBirthIcon.svg";
import AddressIcon from "@assets/svg/icons/addressIcon.svg";
import CheckIcon from "@assets/svg/icons/checkSmall.svg";
import Svg from "@natera/material/lib/svg";
import { messages } from "./messages";
import MpxAddressSuggestionForm from "@app/components/forms/mpxAddressForm/mpxAddressSuggestionForm";
import { DobInput } from "@app/components/forms";

import "./profileFormAccordion.scss";

interface FieldConfig {
  readOnly?: boolean;
  expanded?: boolean;
}

interface SectionVisibility {
  showAlternativeLastName?: boolean;
  showDateOfBirth?: boolean;
  showAddress?: boolean;
  showMobileNumber?: boolean;
}

interface ProfileFormAccordionProps {
  names?: {
    firstName: string;
    middleName: string;
    lastName: string;
    alternativeLastName?: string;
  };
  dateOfBirth?: string;
  onNamesChange?: React.ChangeEventHandler<HTMLInputElement>;
  onNamesUpdate?: React.ChangeEventHandler<HTMLInputElement>;
  onDateOfBirthChange?: (date: string) => void;

  zipCode: string;
  onZipCodeChange: (zipCode: string) => void;
  street1: string;
  onStreet1Change: (street1: string) => void;
  apartmentUnit: string;
  onApartmentUnitChange: (apartmentUnit: string) => void;
  city: string;
  onCityChange: (city: string) => void;
  state: string;
  onStateChange: (state: string) => void;

  mobileNumber?: string;
  onMobileNumberChange?: (phone: string) => void;
  nameConfig?: FieldConfig & { lastNameReadOnly?: boolean };
  dateOfBirthConfig?: FieldConfig;
  mobileNumberConfig?: FieldConfig;
  addressConfig?: FieldConfig;
  sections?: SectionVisibility;
  currentPhone?: string;
  getValidationError: GetValidationError;
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;
  initialDateOfBirth?: string;
  maxNameLength?: number;
  withPadding?: boolean;
  validateAge?: boolean;
  showNameHints?: boolean;
}

const ProfileFormAccordion: FC<ProfileFormAccordionProps> = ({
  names,
  dateOfBirth,
  mobileNumber,
  onNamesChange,
  onNamesUpdate,
  onDateOfBirthChange,
  street1,
  onStreet1Change,
  apartmentUnit,
  onApartmentUnitChange,
  city,
  onCityChange,
  state,
  onStateChange,
  zipCode,
  onZipCodeChange,
  onMobileNumberChange,
  nameConfig = {},
  dateOfBirthConfig = {},
  mobileNumberConfig = {},
  addressConfig = {},
  sections = {},
  currentPhone,
  getValidationError,
  setValidationError,
  clearValidationError,
  initialDateOfBirth,
  maxNameLength = 255,
  withPadding = true,
  validateAge = true,
  showNameHints = false,
}) => {
  const { expanded: expandName = true, lastNameReadOnly = false } = nameConfig;
  const {
    readOnly: dateOfBirthReadOnly = false,
    expanded: expandDateOfBirth = false,
  } = dateOfBirthConfig;
  const {
    readOnly: mobileNumberReadOnly = false,
    expanded: expandMobileNumber = false,
  } = mobileNumberConfig;
  const { expanded: expandAddress = false } = addressConfig;

  const {
    showAlternativeLastName = true,
    showDateOfBirth = true,
    showAddress = true,
    showMobileNumber = true,
  } = sections;
  const intl = useIntl();

  const detailsRefs = {
    name: useRef<HTMLDetailsElement>(null),
    dateOfBirth: useRef<HTMLDetailsElement>(null),
    address: useRef<HTMLDetailsElement>(null),
    mobileNumber: useRef<HTMLDetailsElement>(null),
  };

  const handleNamesChangeWithClearError: React.ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    if (clearValidationError && e.target.value) {
      clearValidationError(e.target.name);
    }
    if (onNamesChange) {
      onNamesChange(e);
    }
  };

  const handleStreet1ChangeWithClearError = (newStreet1: string) => {
    if (clearValidationError && newStreet1) {
      clearValidationError("street1");
    }
    if (onStreet1Change) {
      onStreet1Change(newStreet1);
    }
  };

  const handleCityChangeWithClearError = (newCity: string) => {
    if (clearValidationError && newCity) {
      clearValidationError("city");
    }
    if (onCityChange) {
      onCityChange(newCity);
    }
  };

  const handleStateChangeWithClearError = (newState: string) => {
    if (clearValidationError && newState) {
      clearValidationError("state");
    }
    if (onStateChange) {
      onStateChange(newState);
    }
  };

  const handleZipCodeChangeWithClearError = (newZipCode: string) => {
    if (clearValidationError && newZipCode) {
      clearValidationError("zipCode");
    }
    if (onZipCodeChange) {
      onZipCodeChange(newZipCode);
    }
  };

  const handleApartmentUnitChangeWithClearError = (
    newApartmentUnit: string
  ) => {
    if (clearValidationError && newApartmentUnit) {
      clearValidationError("apartmentUnit");
    }
    if (onApartmentUnitChange) {
      onApartmentUnitChange(newApartmentUnit);
    }
  };

  const handleMobileNumberChangeWithClearError = (newPhone: string) => {
    if (clearValidationError && newPhone) {
      clearValidationError("mobileNumber");
    }
    if (onMobileNumberChange) {
      onMobileNumberChange(newPhone);
    }
  };

  useEffect(() => {
    if (
      (getValidationError("firstName") || getValidationError("lastName")) &&
      detailsRefs.name.current
    ) {
      detailsRefs.name.current.open = true;
    }

    if (
      (getValidationError("dateOfBirth") ||
        getValidationError("dateOfBirthMonth") ||
        getValidationError("dateOfBirthDay") ||
        getValidationError("dateOfBirthYear") ||
        getValidationError("dateOfBirthRequired")) &&
      detailsRefs.dateOfBirth.current &&
      showDateOfBirth
    ) {
      detailsRefs.dateOfBirth.current.open = true;
    }

    if (
      (getValidationError("zipCode") ||
        getValidationError("street1") ||
        getValidationError("city") ||
        getValidationError("state")) &&
      detailsRefs.address.current &&
      showAddress
    ) {
      detailsRefs.address.current.open = true;
    }

    if (
      getValidationError("mobileNumber") &&
      detailsRefs.mobileNumber.current &&
      showMobileNumber
    ) {
      detailsRefs.mobileNumber.current.open = true;
    }
  }, [getValidationError, showDateOfBirth, showAddress, showMobileNumber]);

  const isSectionValid = (
    fieldConfigs: Array<{
      field: string;
      getValue: () => string | undefined;
      required?: boolean;
    }>
  ) => {
    return fieldConfigs.every(({ field, getValue, required = true }) => {
      const hasError = getValidationError(field);

      if (hasError) return false;

      if (required) {
        const value = getValue();
        return value && value.trim().length > 0;
      }
      return true;
    });
  };

  const isNameSectionValid = isSectionValid([
    {
      field: "firstName",
      getValue: () => names?.firstName,
    },
    {
      field: "lastName",
      getValue: () => names?.lastName,
    },
  ]);

  const isDateOfBirthSectionValid = isSectionValid([
    {
      field: "dateOfBirth",
      getValue: () => dateOfBirth,
    },
  ]);

  const isAddressSectionValid = isSectionValid([
    {
      field: "street1",
      getValue: () => street1,
    },
    {
      field: "city",
      getValue: () => city,
    },
    {
      field: "state",
      getValue: () => state,
    },
    {
      field: "zipCode",
      getValue: () => zipCode,
    },
  ]);

  const isPhoneSectionValid = isSectionValid([
    {
      field: "mobileNumber",
      getValue: () => mobileNumber,
    },
  ]);

  return (
    <>
      <details open={expandName} ref={detailsRefs.name}>
        <summary>
          <Svg content={PatientIcon} className="summary__icon" />
          {intl.formatMessage(messages.profileFormName)}
          {isNameSectionValid && (
            <Svg content={CheckIcon} className="summary__check" />
          )}
          <Svg content={ArrowDownGrayIcon} className="summary__arrow" />
        </summary>
        <FormField
          label={intl.formatMessage(messages.profileFormFirstName)}
          required
          error={getValidationError("firstName")}
          htmlFor="firstName"
          withPadding={withPadding}
          helpText={
            showNameHints &&
            intl.formatMessage(messages.profileFormFirstNameHint)
          }
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Textfield
              placeholder={intl.formatMessage(
                messages.profileFormFirstNamePlaceholder
              )}
              id="firstName"
              name="firstName"
              value={names?.firstName}
              onChange={handleNamesChangeWithClearError}
              onBlur={onNamesUpdate}
              outline
              required
              maxLength={maxNameLength}
            />
          </HeapPHIDecorator>
        </FormField>
        <FormField
          label={intl.formatMessage(messages.profileFormMiddleName)}
          error={getValidationError("middleName")}
          htmlFor="middleName"
          withPadding={withPadding}
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Textfield
              placeholder={intl.formatMessage(
                messages.profileFormMiddleNamePlaceholder
              )}
              id="middleName"
              name="middleName"
              value={names?.middleName}
              onChange={handleNamesChangeWithClearError}
              onBlur={onNamesUpdate}
              outline
              maxLength={maxNameLength}
            />
          </HeapPHIDecorator>
        </FormField>
        <FormField
          label={intl.formatMessage(messages.profileFormLastName)}
          required
          error={getValidationError("lastName")}
          htmlFor="lastName"
          withPadding={withPadding}
          helpText={
            showNameHints &&
            intl.formatMessage(messages.profileFormLastNameHint)
          }
        >
          <HeapPHIDecorator protectAttr={["value"]}>
            <Textfield
              placeholder={intl.formatMessage(
                messages.profileFormLastNamePlaceholder
              )}
              id="lastName"
              name="lastName"
              value={names?.lastName}
              onChange={handleNamesChangeWithClearError}
              onBlur={onNamesUpdate}
              outline
              required
              maxLength={maxNameLength}
              disabled={lastNameReadOnly}
            />
          </HeapPHIDecorator>
        </FormField>
        {showAlternativeLastName && (
          <FormField
            label={intl.formatMessage(messages.profileFormAlternativeLastName)}
            error={getValidationError("alternativeLastName")}
            htmlFor="alternativeLastName"
            withPadding={withPadding}
          >
            <HeapPHIDecorator protectAttr={["value"]}>
              <Textfield
                placeholder={intl.formatMessage(
                  messages.profileFormAlternativeLastNamePlaceholder
                )}
                id="alternativeLastName"
                name="alternativeLastName"
                value={names?.alternativeLastName}
                onChange={handleNamesChangeWithClearError}
                onBlur={onNamesUpdate}
                outline
                maxLength={maxNameLength}
              />
            </HeapPHIDecorator>
          </FormField>
        )}
      </details>
      {showDateOfBirth && (
        <>
          <hr className="divider" />
          <details ref={detailsRefs.dateOfBirth} open={expandDateOfBirth}>
            <summary>
              <Svg content={DateOfBirthIcon} className="summary__icon" />
              {intl.formatMessage(messages.profileFormDateOfBirth)}
              {isDateOfBirthSectionValid && (
                <Svg content={CheckIcon} className="summary__check" />
              )}
              <Svg content={ArrowDownGrayIcon} className="summary__arrow" />
            </summary>
            <FormField
              label={intl.formatMessage(messages.profileFormDateOfBirth)}
              required
              withPadding={withPadding}
              htmlFor="dateOfBirthMonth"
            >
              <DobInput
                getValidationError={getValidationError}
                setValidationError={setValidationError}
                clearValidationError={clearValidationError}
                onChange={onDateOfBirthChange}
                defaultValue={initialDateOfBirth}
                showLabels={false}
                validateAge={validateAge}
                disabled={dateOfBirthReadOnly}
              />
            </FormField>
          </details>
        </>
      )}
      {showAddress && (
        <>
          <hr className="divider" />
          <details ref={detailsRefs.address} open={expandAddress}>
            <summary>
              <Svg content={AddressIcon} className="summary__icon" />
              {intl.formatMessage(messages.profileFormAddress)}
              {isAddressSectionValid && (
                <Svg content={CheckIcon} className="summary__check" />
              )}
              <Svg content={ArrowDownGrayIcon} className="summary__arrow" />
            </summary>
            <MpxAddressSuggestionForm
              street1={street1}
              onStreet1Change={handleStreet1ChangeWithClearError}
              zipCode={zipCode}
              onZipCodeChange={handleZipCodeChangeWithClearError}
              apartmentUnit={apartmentUnit}
              onApartmentUnitChange={handleApartmentUnitChangeWithClearError}
              city={city}
              onCityChange={handleCityChangeWithClearError}
              state={state}
              onStateChange={handleStateChangeWithClearError}
              getValidationError={getValidationError}
              setValidationError={setValidationError}
              className={"mpx-address-form__with-margin-top"}
            />
          </details>
        </>
      )}
      {showMobileNumber && (
        <>
          <hr className="divider" />
          <details
            ref={detailsRefs.mobileNumber}
            className="mobile-number-details"
            open={expandMobileNumber}
          >
            <summary>
              <Svg content={MobilePhoneIcon} className="summary__icon" />
              {intl.formatMessage(messages.profileFormMobileNumber)}
              {isPhoneSectionValid && (
                <Svg content={CheckIcon} className="summary__check" />
              )}
              <Svg content={ArrowDownGrayIcon} className="summary__arrow" />
            </summary>
            <PhoneField
              getValidationError={getValidationError}
              withPadding={withPadding}
              currentPhone={currentPhone}
              onPhoneValueChange={handleMobileNumberChangeWithClearError}
              disabled={mobileNumberReadOnly}
            />
          </details>
        </>
      )}
    </>
  );
};

export default ProfileFormAccordion;
